/**
 * Created: 2026-10-06
 * Initial commit: feat(invoice): share order detail rendering between order grids
 *
 * The multi-line 详情 cell shared by the orders grid and the invoice request
 * dialog's orders grid.
 */

import { Box, Chip } from '@mui/material'
import { getWeekdayText } from '@tc-app/shared/booking/getWeekdayText'
import {
  type MergedSlot,
  mergeConsecutiveSlots,
} from '@tc-app/shared/booking/mergeConsecutiveSlots'
import { GAME_FORMAT_CHIP } from '@tc-app/shared/labels'
import { format } from 'date-fns'
import type { GameFormat } from '../gql/graphql'
import { getWeekdayText as getWeekdayTextLocal } from '../lib/dateUtils'

/** The `detail` fields selected by the order list queries. */
export type OrderDetailFields =
  | {
      __typename: 'SlotBookingList'
      items: { courtName: string; startTime: string; price: number | string }[]
    }
  | {
      __typename: 'EventSignupDetail'
      eventName: string
      gameFormat: GameFormat
      startTime: string
      duration: number
    }
  | { __typename: 'CourseDetail'; courseName: string; hours: number }
  | null
  | undefined

function formatMergedSlotName(slot: MergedSlot): string {
  const weekday = getWeekdayText(slot.date)
  return `${slot.courtName}, ${slot.date} ${weekday} ${slot.startHour.toString().padStart(2, '0')}:00-${slot.endHour.toString().padStart(2, '0')}:00`
}

function formatDetail(detail: OrderDetailFields): string {
  if (!detail) return '-'
  if (detail.__typename === 'SlotBookingList') {
    const merged = mergeConsecutiveSlots(
      detail.items.map((d) => ({
        courtId: d.courtName, // use courtName as grouping key since courtId is not in list query
        courtName: d.courtName,
        startTime: d.startTime,
        price: Number(d.price),
      })),
    )
    return merged.map(formatMergedSlotName).join('\n')
  }
  if (detail.__typename === 'EventSignupDetail') {
    const start = new Date(detail.startTime)
    const hour = format(start, 'HH')
    const endHour = format(new Date(start.getTime() + detail.duration * 3600000), 'HH')
    const dateStr = format(start, 'yyyy-MM-dd')
    const weekday = getWeekdayTextLocal(dateStr)
    return `${detail.eventName}\n${dateStr} ${weekday} ${hour}:00-${endHour}:00`
  }
  if (detail.__typename === 'CourseDetail') {
    return `${detail.courseName}\n${detail.hours}课时`
  }
  return '-'
}

export default function OrderDetailCell({ detail }: { detail: OrderDetailFields }) {
  if (detail?.__typename === 'EventSignupDetail') {
    const chip = GAME_FORMAT_CHIP[detail.gameFormat]
    return (
      <Box sx={{ whiteSpace: 'pre-line' }}>
        <Chip
          label={chip.label}
          color={chip.color}
          size="small"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
        {formatDetail(detail)}
      </Box>
    )
  }
  return <Box sx={{ whiteSpace: 'pre-line' }}>{formatDetail(detail)}</Box>
}
